import React, { useEffect, useMemo, useState } from 'react';

import useFilters from '../../../utilities/hooks/useFilters';
import { WidgetApi } from '../../../shared/service/widgetApi';

import { TableMetaData, WidgetDefinition } from '../../../shared/types/widget';
import ArrowIcon from '../../../assets/icons/General/arrow.svg?react';
import useFetch from '../../../utilities/hooks/useFetch';
import { Typography } from '../../widgets/Typography';
import Button from '../../widgets/Button';
import { MoveRightIcon } from '../../../assets/icons/MoveRight';
import { MoveLeftIcon } from '../../../assets/icons/MoveLeft';

type Props = {
  definition: WidgetDefinition;
  content: TableMetaData;
};

const ITEMS_PER_PAGE = 100;

function Table({ definition, content }: Props) {
  const { id } = definition;
  const [isLoading, setIsLoading] = useState(false);

  const [sortKey, setSortKey] = useState('');
  const defaultSort = content?.meta?.sort?.defaultKey;
  const [sortOrder, setSortOrder] = useState({
    order: 'desc',
    key: defaultSort,
  });
  const [currentSort, setCurrentSort] = useState('');
  const [tableData, setTableData] = useState<
    { contentTitle: string; title: string; value: string }[]
  >([]);
  const [currentPage, setCurrentPage] = React.useState(1);

  const totalPages = Math.ceil((tableData?.length || 0) / ITEMS_PER_PAGE);

  const { getFilters } = useFilters();

  const handlePageChange = (newPage: any) => {
    refetchPagination();
    setIsLoading(true);
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const additionalParams = {
    page: currentPage,
    sort: sortKey,
    order: sortOrder.order,
    pageSize: ITEMS_PER_PAGE,
  };

  const { refetch: refetchPagination } = useFetch(
    ['table-pagination'],
    () => WidgetApi(id, getFilters(), additionalParams),
    {
      enabled: false,
      onSuccess: (res) => {
        setTableData(res?.data);
        setIsLoading(false);
      },
      onError: () => {},
    },
  );

  useEffect(() => {
    setTableData(content?.data || []);
  }, [content]);

  const displayedData = tableData?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  function handleSort(item: any) {
    setCurrentSort(item.key);
    setIsLoading(true);
    setSortKey(item?.key);
    setSortOrder((prevSortOrder) => {
      if (prevSortOrder.key == item.key) {
        return {
          order: prevSortOrder.order === 'asc' ? 'desc' : 'asc',
          key: item.key,
        };
      } else {
        return {
          order: 'asc',
          key: item.key,
        };
      }
    });
    setTimeout(() => {
      refetchPagination();
    }, 100);
  }

  useEffect(() => {
    if (currentSort === '' && defaultSort) {
      setCurrentSort(defaultSort);
    }
  }, [currentSort, defaultSort]);

  return (
    <div className="x-4 mb-6 w-full overflow-auto rounded-xl border">
      <div className="relative min-w-[550px] border-collapse">
        <div className="border-taupe-550 bg-taupe-900 absolute -top-[51px] z-[9] flex w-full rounded-t-xl border-b text-left text-sm font-bold text-white">
          {content?.meta?.headers.map((item) => (
            <div
              key={item.key}
              className="border-taupe-550 w-full border-r px-6 py-3 last:border-none"
            >
              <div
                className={`hello hover:bg-black-600 active:bg-black-700 relative -left-2 flex w-fit cursor-pointer items-center gap-x-[7.33px] rounded-[33px] px-2 py-1 transition-all duration-150 select-none`}
                onClick={() => {
                  handleSort(item);
                }}
              >
                <span>{item.label} </span>
                {currentSort === item.key && (
                  <span
                    className={`${sortOrder.order == 'asc' && 'rotate-180'}`}
                  >
                    <ArrowIcon className={`stroke-white`} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-[51px] max-h-[315px] overflow-y-auto border-amber-700">
          {displayedData?.map((row: any, rowIndex) => (
            <div
              key={rowIndex}
              className="border-taupe-550 text-black-900 flex w-full border-b text-sm font-medium last:border-none"
            >
              {content?.meta?.headers.map((header, index) => (
                <div
                  key={header.key}
                  className={`w-full border-r px-6 py-4 last:border-none ${
                    displayedData.length > 6 && index != 2 && 'pr-9'
                  }`}
                  style={{
                    overflowWrap: 'anywhere',
                  }}
                >
                  {row[header.key]}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {(content?.data?.length || 0) > ITEMS_PER_PAGE && (
        <div className="border-taupe-550 flex flex-row items-center justify-between border-t px-6 py-2.5">
          <Button
            className="min-w-32"
            disabled={currentPage === 1}
            leftIcon={<MoveLeftIcon className="text-grey-700 h-4 w-4" />}
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </Button>
          <Typography
            variant="body-sm"
            weight="medium"
            className="!text-grey-800"
          >
            Page {currentPage} of {totalPages}
          </Typography>
          <Button
            className="min-w-32"
            rightIcon={<MoveRightIcon className="text-grey-700 h-4 w-4" />}
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default Table;
